
import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Pin, MoreHorizontal, Edit, Trash2, Share2, Calendar, CheckSquare } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { NoteChecklistIndicator } from './NoteChecklistIndicator';
import { NoteContextMenu } from './NoteContextMenu';

type Note = {
  id: string;
  title: string;
  content: any;
  category: 'general' | 'pinned' | 'ideas' | 'reminders';
  color: string;
  visibility: 'private' | 'board_shared';
  is_pinned: boolean;
  reminder_date?: string;
  reminder_sent: boolean;
  position: number;
  tags: string[];
  created_at: string;
  updated_at: string;
  board_id?: string;
  user_id: string;
};

interface NoteCardProps {
  note: Note;
  viewMode: 'grid' | 'list';
  isDragging?: boolean;
  onEdit: () => void;
  onRefresh: () => void;
}

export const NoteCard = ({ note, viewMode, isDragging, onEdit, onRefresh }: NoteCardProps) => {
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);

  const extractTextContent = (content: any): string => {
    if (typeof content === 'string') {
      return content;
    }
    if (content && content.type === 'doc' && content.content) {
      const extractText = (nodes: any[]): string => {
        let text = '';
        for (const node of nodes) {
          if (node.type === 'text') {
            text += node.text || '';
          } else if (node.content) {
            text += extractText(node.content);
          }
        }
        return text;
      };
      return extractText(content.content);
    }
    return '';
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('reminder_notes')
        .delete()
        .eq('id', note.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Note deleted successfully",
      });
      onRefresh();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast({
        title: "Error",
        description: "Failed to delete note",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const contentText = extractTextContent(note.content);
  const hasReminder = note.reminder_date && !note.reminder_sent;
  const isOverdue = hasReminder && new Date(note.reminder_date!) < new Date();

  if (viewMode === 'list') {
    return (
      <NoteContextMenu note={note} onEdit={onEdit} onRefresh={onRefresh}>
        <Card 
          className={cn(
            "cursor-pointer transition-all hover:shadow-md",
            isDragging && "opacity-50 rotate-2",
            isOverdue && "border-red-200 bg-red-50"
          )}
          style={{ backgroundColor: note.color }}
          onClick={onEdit}
        >
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-medium text-foreground truncate">{note.title}</h3>
                {note.is_pinned && (
                  <Pin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                )}
                <Badge variant="outline" className="text-xs">
                  {note.category}
                </Badge>
              </div>
              
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="truncate">{contentText}</span>
                <NoteChecklistIndicator noteId={note.id} compact />
              </div>
              
              <div className="flex items-center gap-2 mt-2">
                {note.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
                {hasReminder && (
                  <Badge variant={isOverdue ? "destructive" : "outline"} className="text-xs">
                    <Calendar className="h-3 w-3 mr-1" />
                    {format(new Date(note.reminder_date!), 'MMM d')}
                  </Badge>
                )}
              </div>
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 w-8 p-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem onClick={onEdit}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Note</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete this note? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? 'Deleting...' : 'Delete'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>
      </NoteContextMenu>
    );
  }

  return (
    <NoteContextMenu note={note} onEdit={onEdit} onRefresh={onRefresh}>
      <Card 
        className={cn(
          "cursor-pointer transition-all hover:shadow-md",
          isDragging && "opacity-50 rotate-2",
          isOverdue && "border-red-200 bg-red-50"
        )}
        style={{ backgroundColor: note.color }}
        onClick={onEdit}
      >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-foreground truncate">{note.title}</h3>
            {note.is_pinned && (
              <Pin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            )}
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onClick={onEdit}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Note</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete this note? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground line-clamp-3">
            {contentText}
          </p>
          
          <div className="flex items-center justify-between">
            <Badge variant="outline" className="text-xs">
              {note.category}
            </Badge>
            
            <div className="flex items-center gap-2">
              {note.visibility === 'board_shared' && (
                <Share2 className="h-4 w-4 text-muted-foreground" />
              )}
              {hasReminder && (
                <Badge variant={isOverdue ? "destructive" : "outline"} className="text-xs">
                  <Calendar className="h-3 w-3 mr-1" />
                  {format(new Date(note.reminder_date!), 'MMM d')}
                </Badge>
              )}
            </div>
          </div>
          
          <NoteChecklistIndicator noteId={note.id} />
          
          {note.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {note.tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
    </NoteContextMenu>
  );
};
